import { LossWhy, RiskMeter, riskFromPrice } from "./Risk";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { API, errText } from "@/lib/auth";
import { money } from "@/components/market/Shared";
import { cents } from "./utils";

export const Positions = ({ rows, onOpen, onSell, onCopy, canCopy, prefix }) => (
  <div className="divide-y divide-[var(--line)]">
    {rows.map((b) => (
      <div key={b.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`${prefix}-pos-${b.id}`}>
        <button onClick={() => onOpen(b.slug)} className="flex-1 min-w-0 text-left">
          <div className="truncate">{b.label}</div>
          <div className="text-[11px] text-slate-500 font-num flex items-center gap-2">{b.contracts} × {b.side_name} @ {cents(b.entry_price)} · cost {money(b.cost, 2)} <RiskMeter level={riskFromPrice(b.entry_price)} compact /></div>
          {b.status !== "open" && <LossWhy b={b} testid={`${prefix}-loss-why-${b.id}`} />}
        </button>
        <div className={`font-num text-right text-xs ${(b.pnl ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
          {b.pnl == null ? "—" : `${b.pnl >= 0 ? "+" : ""}${money(b.pnl, 2)}`}
          {b.status !== "open" && <div className="text-slate-500">{b.status}</div>}
        </div>
        {onCopy && b.status === "open" && canCopy && !canCopy(b) && <span className="text-[10px] text-slate-500 shrink-0" title="Unlock a higher mode to copy this pick" data-testid={`${prefix}-copy-locked-${b.id}`}>Locked</span>}
        {onCopy && b.status === "open" && (!canCopy || canCopy(b)) && <button onClick={() => onCopy(b)} className="text-xs px-3 py-1 rounded-full border border-[var(--violet)]/60 text-[var(--violet)] hover:bg-[var(--violet)]/10" data-testid={`${prefix}-copy-${b.id}`}>Copy</button>}
        {onSell && b.status === "open" && <button onClick={() => onSell(b.id)} className="text-xs px-3 py-1 rounded-full border border-[var(--line)] hover:border-[var(--cyan)]" data-testid={`${prefix}-sell-${b.id}`}>Sell</button>}
      </div>
    ))}
  </div>
);

export const Summary = ({ d, testid }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-num" data-testid={testid}>
    {[["Cash", money(d.balance, 2)], ["Total value", money(d.equity, 2)], ["Realized P&L", money(d.stats.realized_pnl, 2)],
      ["Win rate", d.stats.win_rate == null ? "no settled bets yet" : `${d.stats.win_rate}% of ${d.stats.closed}`]].map(([k, v]) => (
      <div key={k} className="rounded-xl bg-white/[0.03] border border-[var(--line)] p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="mt-1 text-slate-100 text-sm">{v}</div></div>
    ))}
  </div>
);

export const PaperPortfolio = ({ onOpen }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/predict/paper`).then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("paper-changed", load);
    const id = setInterval(load, 60000);
    return () => { window.removeEventListener("paper-changed", load); clearInterval(id); };
  }, [load]);
  const sell = async (id) => {
    try { const { data } = await axios.post(`${API}/predict/paper/sell/${id}`); toast.success(`Sold for ${money(data.received, 2)} (P&L ${money(data.pnl, 2)})`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const reset = async () => { if (window.confirm("Reset practice balance to $1,000 and clear all practice bets?")) { await axios.post(`${API}/predict/paper/reset`); load(); } };
  if (!d) return null;
  return (
    <section className="glass p-5 space-y-4" data-testid="paper-portfolio">
      <div className="flex items-center justify-between"><h2 className="font-head font-semibold flex items-center gap-2"><Wallet className="w-4 h-4 text-[var(--cyan)]" />My practice bets</h2>
        <button onClick={reset} className="text-xs text-slate-500 underline" data-testid="paper-reset-btn">Reset</button></div>
      <Summary d={d} testid="paper-summary" />
      {d.open.length ? <Positions rows={d.open} onOpen={onOpen} onSell={sell} prefix="paper" /> : <p className="text-sm text-slate-400" data-testid="paper-empty">No open practice bets. Tap any market to place one.</p>}
      {d.closed.length > 0 && <details><summary className="text-xs text-slate-400 cursor-pointer">History ({d.closed.length})</summary><Positions rows={d.closed} onOpen={onOpen} prefix="paper-h" /></details>}
    </section>
  );
};
